/**
 * API Client for Medical Notes Session Management
 * Handles session CRUD operations with JWT authentication
 */

import { refreshAccessToken } from './authApi';
import { isPublicPath } from '../utils/publicPaths';

export interface Session {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  session_metadata?: any;
  extra_metadata?: {
    recording_url?: string;
    fhir_doc_id?: string;
    fhir_patient_id?: string;
    fhir_encounter_id?: string;
    coding?: CodingResult;
  };
  conversation_history?: Array<{
    speaker: string;
    text: string;
    timestamp?: string;
  }>;
  total_input_tokens?: number;
  total_output_tokens?: number;
  total_stt_time_seconds?: number;
  total_tts_time_seconds?: number;
}

/**
 * CodingResult — schema 2.0.
 *
 * Replaces the legacy candidate-based shape from /api/internal/note-coding.
 * Emitted by the correction pipeline via /api/internal/scribe-correction.
 *
 * Each diagnosis and service ships with:
 *   - a single resolved code (not a ranked candidate list)
 *   - reasoning, HCC info, change-from-original, confirmation/doc flags
 *   - service: coverage, cpt_status, E/M basis, modifiers, bundling alerts
 */
export type CptStatus =
  | 'CORRECT'
  | 'INCORRECT'
  | 'OPTIMIZE'
  | 'UPCODED'
  | 'DOWNCODED'
  | 'UNBUNDLED'
  | 'REVIEW'
  | 'RESOLVED_FROM_DESCRIPTION'
  | 'RESOLVED_FROM_DESCRIPTION_FALLBACK'
  | 'CLINICIAN_MODIFIED'
  | null;

export type CoverageStatus =
  | 'covered'
  | 'not_covered'
  | 'requires_authorization'
  | 'pending'
  | 'N/A'
  | null;

export type ChangeFromOriginal = 'added' | 'modified' | 'removed' | 'existing' | null;

export interface CodingDiagnosis {
  item_id: string;
  code: string;
  description: string;
  is_primary: boolean;
  poa_indicator?: string | null;
  hcc_category?: number | null;
  hcc_description?: string | null;
  change_from_original?: ChangeFromOriginal;
  detailed_reasoning?: string;
  missing_documentation?: string | null;
  confirmation_required?: boolean;
  // Comma-separated "CODE: description" alternatives suggested by the coding
  // agent for this diagnosis (display-only in this phase).
  alternate_candidates?: string | null;
}

export interface CodingService {
  item_id: string;
  service_id: string;
  code: string;
  name: string;
  supporting_diagnoses?: string;
  coverage?: CoverageStatus;
  coverage_rejection_reason?: string | null;
  cpt_status?: CptStatus;
  cpt_suggestion?: string | null;
  cpt_reason?: string | null;
  em_coding_basis?: 'MDM' | 'TIME' | 'N/A' | null;
  mdm_complexity?: 'STRAIGHTFORWARD' | 'LOW' | 'MODERATE' | 'HIGH' | 'N/A' | null;
  modifier_suggestion?: string | null;
  bundling_alert?: string | null;
  guideline_reference?: string;
  guideline_impact?: string;
  information_source?: string;
  missing_documentation?: string;
  confirmation_required?: boolean;
}

export type FeedbackRouteType = 'dx' | 'px' | 'conversation';

// ── Two-phase coding bundle types (mirror copilot's EhrCodingResponse) ───────

export type CodingQueryFormat = 'multiple_choice' | 'yes_no' | 'open_ended';

export interface CodingQueryOption {
  option_id: string;
  label: string;
  consequence_summary?: string | null;
}

export interface CodingQueryAnswer {
  selected_option_id?: string | null;
  free_text?: string | null;
  responded_by?: string | null;
  responded_at?: string | null;
}

export interface CodingQuery {
  query_id: string;
  domain: Array<'dx' | 'px'>;
  finding_type?: string;
  question: string;
  rationale: string;
  options: CodingQueryOption[];
  line_ref?: string | null;
  chart_role?: 'primary' | 'secondary' | null;
  scope?: 'encounter' | 'line';
  default_answer_id?: string | null;
  status?: 'open' | 'answered' | 'skipped';
  answer?: CodingQueryAnswer | null;
  format?: CodingQueryFormat | null;
}

/** DX side: a CDI final diagnosis (bundle dx.final_diagnoses[]). */
export interface BundleDxCode {
  code: string;
  description: string;
  justification?: string;
  applies_to_serials?: number[];
  is_primary?: boolean;
  is_new?: boolean;
  guidelines?: string[];
}

/** PX side: a procedure/service line (bundle px.services[]). */
export interface BundlePxCode {
  code?: string | null;
  name?: string | null;
  description?: string | null;
  instructions?: string | null;
  serial_number?: string | null;
  quantity?: number | null;
  modifiers?: string[] | null;
  code_source?: string | null;
  action?: string | null;
  cpt_status?: string | null;
  cpt_suggestion?: string | null;
  cpt_reason?: string | null;
  modifier_suggestion?: string | null;
  modifier_reason?: string | null;
}

/** One answered query for POST /coding/resolve. */
export interface QueryAnswerInput {
  query_id: string;
  option_id?: string;
  free_text?: string;
}

export type BundleStatus =
  | 'pending'
  | 'needs_resolution'
  | 'done'
  | 'degraded'
  | 'error'
  | 'no_content'
  // legacy values from pre-bundle runs still stored on old sessions:
  | 'completed';

/** Session-level lifecycle rollup (B8 vocabulary, shared with EHR worklist). */
export type SessionStatus =
  | 'draft'
  | 'coding'
  | 'needs_resolution'
  | 'coded'
  | 'finalized'
  | 'merged_to_ehr'
  | 'error'
  // legacy:
  | 'amending'
  | 'approved';

export interface FeedbackResponse {
  status: BundleStatus | 'noted';
  applied_at?: string;
  message?: string;
  // P-Session-3 / F1: when feedback fires the conversation route the chart
  // gets a new [[CLINICIAN-ADDED]] block. The endpoint now returns the latest
  // chart text in `note_text` so the frontend can refresh its note pane in
  // one round-trip without an extra GET /api/sessions/{id}.
  note_text?: string;
  note_version?: number;
  routes_fired?: FeedbackRouteType[];
  diagnoses?: BundleDxCode[];
  services?: BundlePxCode[];
  queries?: CodingQuery[];
  has_open_queries?: boolean;
  iteration?: number;
  dx?: any;
  px?: any;
}

export interface CodingResult {
  status: BundleStatus;
  /** Bundle session id — the key for /coding/resolve. */
  session_id?: string;
  /** Legacy alias of session_id (kept for old consumers/runs). */
  correction_id?: string;
  note_id?: string;
  note_version?: number;
  has_open_queries?: boolean;
  iteration?: number;
  queries?: CodingQuery[];
  used_defaults?: string[];
  encounter_summary?: string;
  encounter_type?: string | null;
  /** Raw CDI response block (dx.final_diagnoses is the typed part we render). */
  dx?: { final_diagnoses?: BundleDxCode[]; [k: string]: any } | null;
  /** Raw PX response block (px.services is the typed part we render). */
  px?: { services?: BundlePxCode[]; [k: string]: any } | null;
  dx_error?: string | null;
  px_error?: string | null;
  /** Flat back-compat mirrors of dx.final_diagnoses / px.services. */
  diagnoses: BundleDxCode[] | CodingDiagnosis[];
  services: BundlePxCode[] | CodingService[];
  analysis?: string;
  inpatient_summary?: string | null;
  error?: string;
  coding_runs_count?: number;
  session_status?: SessionStatus;
  /** The note changed after these codes were made — offer a re-run. */
  codes_stale?: boolean;
}

// ── Legacy v1 types retained for any old fixtures / tests ────────────────────
// Safe to delete once no code references them.
export interface CodeResult {
  code: string;
  description: string;
  is_billable?: boolean;
  db_description?: string;
  rationale?: string;
  guideline_note?: string;
}
export interface CodingCandidate extends CodeResult {}

const API_BASE = process.env.REACT_APP_API_URL || '';

class ApiClient {
  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Wrap a fetch in an AbortController so a stalled request can't hang
   * forever. Default timeout is 20s — long enough for a slow first-load
   * but short enough that the user sees an error instead of an eternal
   * spinner. Caller can pass a custom AbortSignal via `options.signal`;
   * if so, both signals are honored (whichever fires first wins).
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit & { timeoutMs?: number } = {}
  ): Promise<Response> {
    const { timeoutMs = 20000, signal: externalSignal, ...rest } = options as any;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
    try {
      return await fetch(url, { ...rest, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async fetchWithAuth(url: string, options?: RequestInit & { timeoutMs?: number }): Promise<Response> {
    const token = this.getToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options?.headers,
    };

    const response = await this.fetchWithTimeout(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    // On 401 try a silent token refresh before giving up
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const retryHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshed}`,
          ...options?.headers,
        };
        const retryResponse = await this.fetchWithTimeout(url, { ...options, headers: retryHeaders, credentials: 'include' });
        // If retry succeeded (any non-401), return as-is — caller decides how
        // to handle non-ok status codes. Only a second 401 means the fresh
        // token is also rejected → full sign-out.
        if (retryResponse.status !== 401) return retryResponse;
      }

      localStorage.removeItem('auth_token');
      // Public routes (incl. the embedded /widget iframe) must never be
      // hard-redirected to the full-page login; the caller surfaces the error.
      if (!isPublicPath()) {
        window.location.href = '/login';
      }
      throw new Error('Session expired');
    }

    return response;
  }

  /**
   * Get all sessions for the authenticated user
   */
  async getSessions(skip: number = 0, limit: number = 50): Promise<Session[]> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions?skip=${skip}&limit=${limit}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch sessions');
    }
    
    return response.json();
  }

  /**
   * Get a specific session by ID with full details
   */
  async getSession(sessionId: string): Promise<Session> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}`
    );
    
    if (!response.ok) {
      const err: any = new Error(response.status === 404 ? 'Session not found' : 'Failed to fetch session');
      err.status = response.status;
      throw err;
    }
    
    return response.json();
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<void> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}`,
      {
        method: 'DELETE',
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to delete session');
    }
  }

  /**
   * Update a session's title
   */
  async updateSessionTitle(sessionId: string, title: string): Promise<Session> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update session');
    }

    return response.json();
  }

  /**
   * Trigger finalize on a session when the WebSocket is already closed
   * (e.g. user paused then clicked End Note). Uploads audio + runs coding.
   */
  async finalizeSession(sessionId: string): Promise<void> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/finalize`,
      { method: 'POST' },
    );
    if (!response.ok) throw new Error('Failed to finalize session');
  }

  /**
   * Get coding agent results for a session.
   * Returns {status: 'pending'} if the coding agent is still running.
   */
  async getCodingResults(sessionId: string): Promise<CodingResult> {
    const response = await this.fetchWithAuth(`${API_BASE}/api/sessions/${sessionId}/coding`);
    if (!response.ok) {
      const err: any = new Error('Failed to fetch coding results');
      err.status = response.status;
      throw err;
    }
    return response.json();
  }

  /**
   * Submit natural-language feedback on a coded session.
   * Response: {status: 'completed'|'noted', applied_at?}.
   */
  async submitCodingFeedback(sessionId: string, feedbackText: string): Promise<FeedbackResponse> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/coding/feedback`,
      // Conversation feedback regenerates the chart + re-codes via copilot, which
      // can take 10–30s+. Use a long timeout (matching the backend copilot client)
      // so the request isn't aborted client-side while the backend is still working.
      { method: 'POST', body: JSON.stringify({ feedback_text: feedbackText }), timeoutMs: 180000 },
    );
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`Feedback failed (${response.status}): ${detail}`);
    }
    return response.json();
  }

  /**
   * Phase 2 of the coding bundle: submit the clinician's query answers
   * (and/or free-text feedback) to /coding/resolve. Returns the updated
   * bundle — which may carry cascade queries (has_open_queries=true,
   * iteration incremented) until finalized.
   */
  async submitQueryAnswers(
    sessionId: string,
    answers: QueryAnswerInput[],
    feedbackText?: string,
  ): Promise<CodingResult> {
    const body: any = { answers };
    if (feedbackText && feedbackText.trim()) body.feedback_text = feedbackText.trim();
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/coding/resolve`,
      // Resolve replays the affected engines — can take 1-3 minutes.
      { method: 'POST', body: JSON.stringify(body), timeoutMs: 600000 },
    );
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`Resolve failed (${response.status}): ${detail}`);
    }
    return response.json();
  }

  /**
   * Re-run coding for a session against its stored chart (e.g. after a
   * transient copilot error). Returns {status:'pending'}; poll for the result.
   */
  async retryCoding(sessionId: string): Promise<{ status: string }> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/coding/retry`,
      { method: 'POST' },
    );
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`Retry coding failed (${response.status}): ${detail}`);
    }
    return response.json();
  }

  /**
   * Push selected billing codes to EHR. Stub — resolves immediately.
   */
  async pushCodesToEhr(
    sessionId: string,
    codes: Array<{ code: string; kind: 'dx' | 'px' }>,
  ): Promise<{ ok: true }> {
    return Promise.resolve({ ok: true });
  }

  /**
   * Publish a completed note as a FHIR DocumentReference.
   */
  async publishToHIS(sessionId: string, fhirPatientId: string, fhirEncounterId: string): Promise<{ status: string; fhir_doc_id: string }> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/publish`,
      {
        method: 'POST',
        body: JSON.stringify({ fhir_patient_id: fhirPatientId, fhir_encounter_id: fhirEncounterId }),
      }
    );
    if (!response.ok) throw new Error('Failed to publish note to HIS');
    return response.json();
  }

  /**
   * Get the structured JSON widget payload for a session.
   */
  /**
   * Typed-first widget flow: create a session with no recording so typed
   * text can generate the first note. A later recording resumes it.
   */
  async createSession(opts: { reportName?: string; fhirPatientId?: string; fhirEncounterId?: string; clientRef?: string } = {}): Promise<{ session_id: string }> {
    const response = await this.fetchWithAuth(`${API_BASE}/api/sessions`, {
      method: 'POST',
      body: JSON.stringify({
        report_name: opts.reportName || 'SOAP_Chart',
        fhir_patient_id: opts.fhirPatientId || '',
        fhir_encounter_id: opts.fhirEncounterId || '',
        // Typed-first draft: the session is born over HTTP, so the host record
        // must be attached here too, not only on the socket.
        client_ref: opts.clientRef || '',
      }),
    });
    if (!response.ok) throw new Error('Failed to create session');
    return response.json();
  }

  /**
   * Voice note feedback (widget): revise the generated note per a free-text
   * instruction. Distinct from submitCodingFeedback — no coding involved.
   * Regen takes 10-30s+, same long timeout as coding feedback.
   */
  async submitNoteFeedback(sessionId: string, feedbackText: string): Promise<{ status: string; note_text: string }> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/note/feedback`,
      { method: 'POST', body: JSON.stringify({ feedback_text: feedbackText }), timeoutMs: 180000 },
    );
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try { detail = (await response.json()).detail || detail; } catch {}
      throw new Error(detail);
    }
    return response.json();
  }

  /**
   * Widget reopen-restore: latest session with a note for this FHIR encounter.
   * Returns null when the encounter has no recorded note yet (404).
   */
  async getSessionByEncounter(fhirEncounterId: string, fhirPatientId?: string): Promise<{ session_id: string; note_text: string; report_name?: string } | null> {
    // Patient id (when known) lets the backend's same-org fallback cross-check
    // it — a reused/placeholder encounter id must never restore another
    // patient's note.
    const q = fhirPatientId ? `?fhir_patient_id=${encodeURIComponent(fhirPatientId)}` : '';
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/encounters/${encodeURIComponent(fhirEncounterId)}/session${q}`
    );
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to look up session for encounter');
    return response.json();
  }

  /** Back-fill FHIR linkage on a session that started before INIT_PATIENT arrived. */
  async setSessionFhir(sessionId: string, fhirPatientId: string, fhirEncounterId: string): Promise<any> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/fhir`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fhir_patient_id: fhirPatientId, fhir_encounter_id: fhirEncounterId }),
      }
    );
    if (!response.ok) throw new Error('Failed to set session FHIR linkage');
    return response.json();
  }

  async getWidgetPayload(sessionId: string, template: string = 'SOAP'): Promise<any> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/widget-payload?template=${template}`
    );
    if (!response.ok) throw new Error('Failed to fetch widget payload');
    return response.json();
  }

  /**
   * Attach EHR chart background to a session (widget INIT_CONTEXT). The note
   * generator weaves it into the prompt as case background.
   */
  async setSessionContext(sessionId: string, chartContext: string): Promise<any> {
    const response = await this.fetchWithAuth(
      `${API_BASE}/api/sessions/${sessionId}/context`,
      {
        method: 'POST',
        body: JSON.stringify({ chart_context: chartContext }),
      }
    );
    if (!response.ok) throw new Error('Failed to set session context');
    return response.json();
  }
}

export const apiClient = new ApiClient();
